import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'constants/routes';
import { Controller, useForm } from 'react-hook-form';
import { Box, Breadcrumbs, FormHelperText, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import RightArrowIcon from '@mui/icons-material/ChevronRightRounded';

import Input from 'shared/components/widgets/input';
import Button from 'shared/components/widgets/button';
import DatePicker from 'shared/components/widgets/date-picker';
import Dialog from 'shared/components/widgets/dialog';
import FieldBox from 'shared/components/widgets/field-box';
import FieldBoxProperty from 'shared/components/widgets/field-box-property';
import UploadFile from 'shared/components/widgets/upload-file';
import FileUploadingDialogContent from 'shared/components/dialog-content/file-uploading';
import RouteLink from 'shared/components/widgets/route-link';
import InputContainer from 'shared/components/widgets/input-container';

import { customerDeliveryFileRegisterService } from '@services/private/customer-delivery/file-register.service';
import { is200xRange } from '@utils/utilities';
import { customerDeliveryFileUploadInitiateService } from '@services/private/customer-delivery/file-upload/file-upload-initiate';
import { customerDeliveryFileUploadUrlAssignService } from '@services/private/customer-delivery/file-upload/file-upload-url-assign';
import { customerDeliveryFileUploadInsertService } from '@services/private/customer-delivery/file-upload/file-upload-insert';
import { customerDeliveryFileUploadCompleteService } from '@services/private/customer-delivery/file-upload/file-upload-complete';
import { VALIDATION_MESSAGE } from 'constants/validation/message';
import { customerDeliveryFileUploadStopService } from '@services/private/customer-delivery/file-upload/file-upload-stop';

const CustomerDeliveryFileRegister = () => {
  const { SUBJECT, EXPIRATION_YMD, AUTH_PWD } =
    VALIDATION_MESSAGE.CUSTOMER_DELIVERY.REGISTER_FILE;
  const navigate = useNavigate();

  const customerDeliveryFileRegisterSchema = yup.object({
    subject: yup
      .string()
      .required(SUBJECT.REQUIRED)
      .min(3, SUBJECT.MIN_LENGTH)
      .max(100, SUBJECT.MAX_LENGTH),
    expirationYmd: yup
      .string()
      .required(EXPIRATION_YMD.REQUIRED)
      .transform((_value, originalValue) => {
        return originalValue ? dayjs(originalValue).format('YYYY-MM-DD') : null;
      })
      .nullable()
      .matches(/^\d{4}-\d{2}-\d{2}$/, EXPIRATION_YMD.ALLOWED_DATE_FORMAT)
      .test('is-not-past-date', '만료일이 지나서 다른 날짜를 선택해 주세요', (value) => {
        if (!value) return true;
        return !dayjs().isAfter(dayjs(value, 'YYYY-MM-DD'), 'day');
      }),
    authPwd: yup
      .string()
      .test('is-empty-or-valid', AUTH_PWD.MIN_LENGTH, (value) => {
        if (value === undefined || value === '') {
          return true;
        }
        return value.length >= 4 && value.length <= 20;
      }),
    file: yup.mixed().required('파일을 업로드 해주세요.'),
  });

  type CustomerDeliveryFileRegisterFormData = yup.InferType<
    typeof customerDeliveryFileRegisterSchema
  >;

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger,
    clearErrors,
  } = useForm<CustomerDeliveryFileRegisterFormData>({
    defaultValues: {
      subject: '',
      expirationYmd: undefined,
      file: undefined,
      authPwd: ''
    },
    resolver: yupResolver(customerDeliveryFileRegisterSchema),
  });
  const uploadedFile = watch('file');
  // const [uploadedFile, setUploadedFile] = useState<any>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [otmSeq, setOtmSeq] = useState('');
  const [otmfSeq, setOtmfSeq] = useState('');
  const [uploadId, setUploadId] = useState('');

  const abortControllersRef = useRef<AbortController[]>([]);

  const [openUploadingDialog, setOpenUploadingDialog] = useState(false);
  const handleOpenUploading = () => setOpenUploadingDialog(true);
  const handleCloseUploading = () => setOpenUploadingDialog(false);

  const resetUploadState = () => {
    setUploadProgress(0); // Reset progress to 0%
    // setUploadedFile(null); // Clear the uploaded file state
    setOtmSeq(''); // Clear the otmSeq
    setOtmfSeq(''); // Clear the otmfSeq
    setUploadId(''); // Clear the uploadId
    clearErrors();
  };

  const stopUploading = async (
    otmSeq: string,
    otmfSeq: string,
    uploadId: string,
  ) => {
    abortControllersRef.current.forEach((controller) => controller.abort());
    abortControllersRef.current = [];
    const { response, statusCode } =
      await customerDeliveryFileUploadStopService({
        otmSeq,
        otmfSeq,
        uploadId,
      });

    if (response?.code === '1' && is200xRange(statusCode)) {
    }
  };

  const registerFile = async (subject, otmSt, expirationYmd, authPwd, file) => {
    resetUploadState();
    const controller = new AbortController();
    abortControllersRef.current.push(controller);

    try {
      const { response, statusCode, error } =
        await customerDeliveryFileRegisterService(
          {
            subject,
            otmSt,
            expirationYmd,
            authPwd,
          },
          controller.signal,
        );

      if (error || !is200xRange(statusCode)) {
        return;
      }

      if (response?.code === '1' && is200xRange(statusCode)) {
        const controller2 = new AbortController();
        abortControllersRef.current.push(controller2);

        const {
          response: uploadInitiateResponse,
          statusCode: uploadInitiateStatusCode,
          error: uploadInitiateError,
        } = await customerDeliveryFileUploadInitiateService(
          {
            otmSeq: response?.result?.otmInfo?.otmSeq,
            fileName: file?.name,
            fileSize: file?.size,
          },
          controller2.signal,
        );

        setOtmSeq(response?.result?.otmInfo?.otmSeq);

        if (uploadInitiateError || !is200xRange(uploadInitiateStatusCode)) {
          handleCloseUploading();
        }

        if (
          uploadInitiateResponse?.code === '1' &&
          is200xRange(uploadInitiateStatusCode)
        ) {
          const { uploadId, partSize, otmfSeq } =
            uploadInitiateResponse?.result?.otmFileInfo;
          const totalParts = Math.ceil(file?.size / partSize);
          const etags: {
            partNumber: number;
            etag: string;
          }[] = [];

          setUploadId(uploadId);
          setOtmfSeq(otmfSeq);

          let totalUploadedBytes = 0;
          for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
            const controller3 = new AbortController();
            abortControllersRef.current.push(controller3);
            const {
              response: uploadUrlAssignResponse,
              statusCode: uploadUrlAssignStatusCode,
              error: uploadUrlAssignError,
            } = await customerDeliveryFileUploadUrlAssignService(
              {
                otmSeq: response?.result?.otmInfo?.otmSeq,
                otmfSeq: uploadInitiateResponse?.result?.otmFileInfo?.otmfSeq,
                uploadId: uploadId,
                partNumber,
              },
              controller3.signal,
            );
            if (uploadUrlAssignError || !is200xRange(uploadUrlAssignStatusCode)) {
              // setUploadedFile(null);
              stopUploading(otmSeq, otmfSeq, uploadId)
              handleCloseUploading();
              navigate(ROUTES.CUSTOMER_DELIVERY_FILES.ROOT);
            }
            if (
              uploadUrlAssignResponse?.code === '1' &&
              is200xRange(uploadUrlAssignStatusCode)
            ) {
              const start = (partNumber - 1) * partSize;
              const end = Math.min(start + partSize, file?.size);
              const filePart = file.slice(start, end);

              const { presignedUrl } =
                uploadUrlAssignResponse?.result?.otmFileInfo;

              const uploadResponse = await fetch(presignedUrl, {
                method: 'PUT',
                body: filePart,
                headers: {
                  'Content-Type': file?.type,
                },
                signal: controller3.signal,
              });

              const etag = uploadResponse.headers.get('ETag')!;

              etags.push({ partNumber, etag });

              await customerDeliveryFileUploadInsertService(
                {
                  otmSeq: response?.result?.otmInfo?.otmSeq,
                  otmfSeq: uploadInitiateResponse?.result?.otmFileInfo?.otmfSeq,
                  partNumber,
                  etag,
                },
                controller3.signal,
              );

              totalUploadedBytes += end - start;
              const progress = Math.trunc(
                (totalUploadedBytes / file.size) * 100,
              );
              setUploadProgress(progress);

              if (totalParts === 1 && partNumber === 1) {
                setUploadProgress(100);
              }
            }
          }
          const {
            response: uploadCompleteResponse,
            statusCode: uploadCompleteStatusCode,
            error: uploadCompleteError,
          } = await customerDeliveryFileUploadCompleteService(
            {
              otmSeq: response?.result?.otmInfo?.otmSeq,
              otmfSeq: uploadInitiateResponse?.result?.otmFileInfo?.otmfSeq,
              partArray: etags,
            },
            controller.signal,
          );

          if (uploadCompleteError || !is200xRange(uploadCompleteStatusCode)) {
            // setUploadedFile(null);
            handleCloseUploading();
          }

          if (
            uploadCompleteResponse?.code === '1' &&
            is200xRange(uploadCompleteStatusCode)
          ) {
            handleCloseUploading();
            navigate(ROUTES.CUSTOMER_DELIVERY_FILES.ROOT);
          }
        }
      }
    }
    catch (error) {
      console.log(error)
    }
  };

  const submit = (data: any) => {
    const { subject, expirationYmd, authPwd, file } = data;
    registerFile(subject, 'allow', expirationYmd, authPwd, file);
    handleOpenUploading();
  };

  return (
    <Box display="flex" flexDirection="column" flex={1}>
      <Box marginBottom="18px">
        <Breadcrumbs
          aria-label="breadcrumb"
          separator={<RightArrowIcon sx={{ color: 'var(--neutral-800)' }} />}
        >
          <RouteLink to={ROUTES.CUSTOMER_DELIVERY_FILES.ROOT}>Home</RouteLink>
          <RouteLink to={ROUTES.CUSTOMER_DELIVERY_FILES.ROOT}>
            고객 전달용 대용량 파일
          </RouteLink>
          <RouteLink
            isActive
            to={ROUTES.CUSTOMER_DELIVERY_FILES.REGISTER_FILE.path}
          >
            대용량 파일 등록
          </RouteLink>
        </Breadcrumbs>
      </Box>
      <Box marginBottom="20px">
        <Typography variant="h6" color="var(--base-black)" fontWeight={700}>
          고객 전달용 대용량 파일
        </Typography>
      </Box>
      <Box component="form" onSubmit={handleSubmit(submit)}>
        <Box
          display="flex"
          flexDirection="column"
          gap={6}
          marginBottom="50px"
          position="relative"
        >
          {/* BASIC INFORMATION */}
          <FieldBox title="기본 정보">
            {/* UPLOAD TITLE */}
            <FieldBoxProperty
              name="* 업로드 제목"
              value={
                <Controller
                  name="subject"
                  control={control}
                  render={({ field }) => {
                    return (
                      <InputContainer type="form">
                        <Input
                          {...field}
                          placeholder="업로드 제목을 입력해 주세요."
                          showErrorMsg={!!errors.subject}
                          errorMessage={
                            errors.subject && errors.subject.message
                          }
                          value={field.value}
                        />
                      </InputContainer>
                    );
                  }}
                />
              }
            />
            {/* EXPIRATION DATE */}
            <FieldBoxProperty
              name="* 만료일자"
              value={
                <Box display={'flex'} flexDirection={'column'} gap={2}>
                  <Box display="flex" flexDirection="row" gap={2}>
                    <Controller
                      name="expirationYmd"
                      control={control}
                      render={({ field }) => {
                        const { onChange, value, ...restField } = field;
                        return (
                          <DatePicker
                            {...restField}
                            format="YYYY-MM-DD"
                            value={value ? dayjs(value, 'YYYY-MM-DD') : null}
                            open={showDatePicker}
                            closeAction={() => {
                              trigger('expirationYmd')
                              setShowDatePicker(false)
                            }}
                            onChange={(date: Dayjs | null) => {
                              onChange(date ? date.format('YYYY-MM-DD') : null);
                            }}
                            selectToday={() => {
                              onChange(dayjs());
                              setShowDatePicker(false);
                            }}
                          // minDate={dayjs()}
                          // showErrorMsg={!!errors.expirationYmd}
                          // errorMessage={
                          //   errors.expirationYmd && errors.expirationYmd.message
                          // }
                          />
                        );
                      }}
                    />
                    <Box>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => setShowDatePicker(true)}
                      >
                        달력
                      </Button>
                    </Box>
                  </Box>
                  {!!errors.expirationYmd && (
                    <FormHelperText
                      sx={{
                        width: '100%',
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '20px',
                        letterSpacing: '-0.1px',
                        color: Boolean(errors.expirationYmd.message) ? 'var(--danger-500)' : 'var(--gray-50)',
                        margin: 'unset',
                      }}
                    >
                      {Boolean(errors.expirationYmd.message) ? errors.expirationYmd.message : ''}
                    </FormHelperText>
                  )}
                </Box>
              }
            />
            {/* FILE STATUS */}
            <FieldBoxProperty
              name="파일상태"
              type="text"
              value="다운로드 가능"
            />

            {/* PASSWORD */}
            <FieldBoxProperty
              name="패스워드"
              value={
                <Box display={'flex'} alignItems={'center'} gap={2}>
                  <Controller
                    name="authPwd"
                    control={control}
                    render={({ field }) => {
                      return (
                        <InputContainer type="form">
                          <Input
                            {...field}
                            name="password"
                            placeholder="비밀번호를 입력해 주세요."
                            isPassword
                            showErrorMsg={!!errors.authPwd}
                            errorMessage={
                              errors.authPwd && errors.authPwd.message
                            }
                            value={field.value}
                          />
                        </InputContainer>
                      );
                    }}
                  />
                  <Typography
                    color={'var(--danger-700)'}
                    sx={{
                      alignSelf: 'flex-start',
                      paddingTop: '10px',
                    }}
                  >
                    ※ 보안이 필요한 파일에는 비밀번호를 설정해 주십시오.
                  </Typography>
                </Box>
              }
            />
          </FieldBox>

          {/* UPLOAD FILE */}
          <FieldBox title="업로드 파일">
            <FieldBoxProperty
              name="* 업로드 파일"
              value={
                <Box>
                  <UploadFile
                    uploadedFile={uploadedFile}
                    setUploadedFile={(file) => setValue('file', file)}
                  />
                  {!uploadedFile && errors.file && (
                    <Typography color={'var(--danger-500)'}>
                      {errors.file.message}
                    </Typography>
                  )}
                </Box>
              }
            />
          </FieldBox>
        </Box>
        <Box>
          <Box display="flex" gap="14px">
            {/* CANCEL */}
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate(ROUTES.CUSTOMER_DELIVERY_FILES.ROOT)}
            >
              취소
            </Button>
            {/* SAVE */}
            <Button
              variant="contained"
              color="primary"
              type={'submit'}
              // disabled={!isValid}
              disabled={!watch('subject') || !watch('expirationYmd') || !watch('file') || dayjs().isAfter(dayjs(watch('expirationYmd'), 'YYYY-MM-DD'), 'day')}
            >
              저장하기
            </Button>
          </Box>
        </Box>
      </Box>
      <Dialog
        isBackDropClickClose={false}
        open={openUploadingDialog}
        closeAction={handleCloseUploading}
        width={348}
        height={304}
        isCloseIcon={false}
      >
        <FileUploadingDialogContent
          closeAction={handleCloseUploading}
          progress={uploadProgress}
          stopAction={() => stopUploading(otmSeq, otmfSeq, uploadId)}
        />
      </Dialog>
    </Box>
  );
};

export default CustomerDeliveryFileRegister;
